/**
 * Production Configuration for Image Handling
 * This file contains environment-specific settings for production image loading
 */

// Environment detection
export const isProduction = process.env.NODE_ENV === 'production';
export const isDevelopment = process.env.NODE_ENV === 'development';

// Production-specific settings
export const PRODUCTION_CONFIG = {
  // Image loading settings
  imageLoading: {
    retryAttempts: 3,
    retryDelay: 1000, // milliseconds
    timeout: 10000, // 10 seconds
    enableCaching: true,
    enableDebugLogging: false, // Set to true for debugging in production
  },
  
  // Alibaba CDN specific settings
  alibabaCDN: {
    enableCacheBusting: true,
    cacheBustingInterval: 300000, // 5 minutes
    enableHttps: true,
    enableCrossOrigin: true,
    fallbackUrls: [
      'https://via.placeholder.com/300x300?text=Image+Unavailable',
      'https://via.placeholder.com/300x300?text=Loading+Failed'
    ]
  },
  
  // CloudFront CDN specific settings (for translated images)
  cloudfrontCDN: {
    enableCacheBusting: false, // Don't modify CloudFront URLs
    enableHttps: true,
    enableCrossOrigin: true,
    fallbackUrls: [
      'https://via.placeholder.com/300x300?text=Translated+Image+Unavailable',
      'https://via.placeholder.com/300x300?text=Translation+Failed'
    ]
  },
  
  // Error handling
  errorHandling: {
    showFallbackImages: true,
    logErrors: true,
    enableRetry: true,
    maxRetries: 3
  }
};

// Development-specific settings
export const DEVELOPMENT_CONFIG = {
  imageLoading: {
    retryAttempts: 2,
    retryDelay: 500,
    timeout: 5000,
    enableCaching: false,
    enableDebugLogging: true,
  },
  
  alibabaCDN: {
    enableCacheBusting: false,
    cacheBustingInterval: 60000, // 1 minute
    enableHttps: true,
    enableCrossOrigin: true,
    fallbackUrls: [
      'https://via.placeholder.com/300x300?text=Dev+Image+Unavailable'
    ]
  },
  
  // CloudFront CDN specific settings (for translated images)
  cloudfrontCDN: {
    enableCacheBusting: false, // Don't modify CloudFront URLs
    enableHttps: true,
    enableCrossOrigin: true,
    fallbackUrls: [
      'https://via.placeholder.com/300x300?text=Dev+Translated+Image+Unavailable',
      'https://via.placeholder.com/300x300?text=Dev+Translation+Failed'
    ]
  },
  
  errorHandling: {
    showFallbackImages: true,
    logErrors: true,
    enableRetry: true,
    maxRetries: 2
  }
};

// Get current configuration based on environment
export const getCurrentConfig = () => {
  return isProduction ? PRODUCTION_CONFIG : DEVELOPMENT_CONFIG;
};

// Production-specific image URL transformations
export const transformImageUrlForProduction = (url) => {
  if (!url) return '';
  
  let transformedUrl = url;
  
  // Ensure HTTPS in production
  if (isProduction && url.startsWith('http://')) {
    transformedUrl = url.replace('http://', 'https://');
  }
  
  // Add production-specific parameters for Alibaba CDN
  if (isProduction && (url.includes('alicdn.com') || url.includes('alibaba.com'))) {
    const baseUrl = transformedUrl.split('?')[0];
    const timestamp = Date.now();
    transformedUrl = `${baseUrl}?t=${timestamp}&prod=1&v=1`;
  }
  
  // Don't modify CloudFront URLs (translated images) - they should work as-is
  if (url.includes('cloudfront.net')) {
    // Just ensure HTTPS, don't add any parameters
    transformedUrl = transformedUrl.replace(/^http:\/\//, 'https://');
  }
  
  return transformedUrl;
};

// Production debugging utilities
export const productionLog = (message, data = null) => {
  const config = getCurrentConfig();
  
  if (config.imageLoading.enableDebugLogging) {
    if (data) {
      // console.log(`[PROD-IMAGE] ${message}`, data);
    } else {
      // console.log(`[PROD-IMAGE] ${message}`);
    }
  }
};

export const productionError = (message, error = null) => {
  const config = getCurrentConfig();
  
  if (config.errorHandling.logErrors) {
    if (error) {
      console.error(`[PROD-IMAGE-ERROR] ${message}`, error);
    } else {
      console.error(`[PROD-IMAGE-ERROR] ${message}`);
    }
  }
};

// Environment-specific image loading strategies
export const getImageLoadingStrategy = () => {
  const config = getCurrentConfig();
  
  return {
    retryCount: config.imageLoading.retryAttempts,
    retryDelay: config.imageLoading.retryDelay,
    timeout: config.imageLoading.timeout,
    enableCaching: config.imageLoading.enableCaching,
    enableDebugLogging: config.imageLoading.enableDebugLogging,
    fallbackUrls: config.alibabaCDN.fallbackUrls
  };
};
